import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { placesSearchOptions } from '../utils/placesBias';
import { PreferredLocation, preferredLocationFromResult } from './preferredLocation';
import { useListingTotal } from '../utils/listingTotal';
import './SearchInputBox.scss';
import { useTranslation } from 'react-i18next';
import { useGoogleMapsApi } from '../../GoogleMapsApiContext';
import { LatLng } from '../utils/types';
import {
  GeolocationFailure,
  getCurrentPosition,
  toGeolocationFailure,
} from '../utils/geolocation';
import { HERE_MAX_M, NearbyPlaceLabel, pickDistrictRows } from '../HouseMap/placeLabels';
import { LocationArrowIcon } from '../AppContent/NewLandingPage/AddMyPlaceFlow/icons';
import {
  BuildingChipIcon,
  DistrictChipIcon,
} from '../AppContent/NewLandingPage/CouldBuyFlow/locationIcons';

type SearchResultBounds = {
  sw: [number, number];
  ne: [number, number];
  maxZoom: number;
};

const BUILDING_MAX_ZOOM = 18;
const ROUTE_MAX_ZOOM = 17;
const NEIGHBORHOOD_MAX_ZOOM = 15;
const POSTAL_MAX_ZOOM = 15;
const LOCALITY_MAX_ZOOM = 13;
// Where the search sends a viewer with no districts around them. The label is
// translated; the query is not, since it goes to Google geocoding.
const SHOWCASE_CITY = 'Helsinki, Finland';
const ADMIN_MAX_ZOOM = 11;
const COUNTRY_MAX_ZOOM = 7;

// True when `address_components` carry both a street_number and a route —
// i.e. a precise civic address (a building / unit). Used as a fallback when
// the result's top-level `types` doesn't already say `street_address`.
const hasStreetAndHouseNumber = (
  components: Array<{ types: string[] }> | undefined,
): boolean => {
  if (!components) return false;
  let hasNumber = false;
  let hasRoute = false;
  for (const c of components) {
    if (c.types.includes('street_number')) hasNumber = true;
    if (c.types.includes('route')) hasRoute = true;
  }
  return hasNumber && hasRoute;
};

// Picks an upper-bound zoom for the chosen result. fitBounds picks the
// natural zoom that fits the rectangle; this only kicks in when that
// natural zoom would otherwise exceed the cap (e.g. when Google's bounds
// is missing and the viewport is unusually tight for the place type).
const computeMaxZoom = (result: {
  types: string[];
  address_components: Array<{ types: string[] }>;
}): number => {
  const types = result.types || [];
  if (
    types.includes('street_address')
    || types.includes('premise')
    || types.includes('subpremise')
    || hasStreetAndHouseNumber(result.address_components)
  ) {
    return BUILDING_MAX_ZOOM;
  }
  if (types.includes('establishment') || types.includes('point_of_interest')) {
    return BUILDING_MAX_ZOOM;
  }
  if (types.includes('route')) return ROUTE_MAX_ZOOM;
  if (
    types.includes('neighborhood')
    || types.includes('colloquial_area')
    || types.includes('postal_town')
    || types.some((t) => t.startsWith('sublocality'))
  ) {
    return NEIGHBORHOOD_MAX_ZOOM;
  }
  if (types.includes('postal_code')) return POSTAL_MAX_ZOOM;
  if (types.includes('locality')) return LOCALITY_MAX_ZOOM;
  if (types.some((t) => t.startsWith('administrative_area'))) return ADMIN_MAX_ZOOM;
  if (types.includes('country')) return COUNTRY_MAX_ZOOM;
  return BUILDING_MAX_ZOOM;
};

// Google's "no extent known" default viewport is ~0.005 degrees square (we
// observe ~0.0027 in practice for some Helsinki neighborhoods). Treat any
// rectangle whose largest dimension is below this as "Google didn't actually
// have a meaningful rectangle for this place" and try to recover via a
// secondary geocode by postal_code.
const DEFAULT_VIEWPORT_THRESHOLD_DEG = 0.005;
// Sanity floor for the secondary postal-code result: a Helsinki postal area
// is reliably bigger than this, so anything smaller is suspicious and we
// fall back to the primary path instead of switching.
const SECONDARY_RECT_MIN_SPAN_DEG = 0.005;

const rectIsRich = (rect: google.maps.LatLngBounds | undefined): boolean => {
  if (!rect) return false;
  const ne = rect.getNorthEast();
  const sw = rect.getSouthWest();
  const lngSpan = ne.lng() - sw.lng();
  const latSpan = ne.lat() - sw.lat();
  return lngSpan >= DEFAULT_VIEWPORT_THRESHOLD_DEG
    || latSpan >= DEFAULT_VIEWPORT_THRESHOLD_DEG;
};

const isAreaResultType = (types: string[] | undefined): boolean => {
  if (!types) return false;
  return types.includes('neighborhood')
    || types.includes('postal_code')
    || types.includes('colloquial_area')
    || types.includes('postal_town')
    || types.some((t) => t.startsWith('sublocality'));
};

const findComponent = (
  components: Array<{ long_name: string; types: string[] }>,
  type: string,
) => components.find((c) => c.types.includes(type)) ?? null;

type SearchInputProps = {
  setMapCenter: (mapCenter: { lat: number; lng: number }) => void;
  setPreferredLocation?: (place: PreferredLocation | null) => void;
  setTriggeredFromSearch: (triggeredFromSearch: boolean) => void;
  setSearchResultBounds?: (bounds: SearchResultBounds | null) => void;
  setSearchAddressComponents?: (
    components: Array<{ long_name: string; types: string[] }>,
  ) => void;
  setCountryOfFeedContent: (country: string) => void;
  countryOfFeedContent: string;
  placeholder?: string;
  isActive: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  debugLabel?: string;
  // Live map centre. Together with `getNearbyPlaces` it makes the empty search
  // show a "Use current location" row and the districts around this point
  // (JAA-358); without them the empty search stays blank.
  viewportCenter?: LatLng | null;
  // Live map zoom. Zooming around a fixed point leaves the centre untouched but
  // changes what is on screen, so the district rows follow it as well.
  viewportZoom?: number | null;
  // District labels around a point, from the map's loaded tiles.
  getNearbyPlaces?: (center: LatLng) => Promise<NearbyPlaceLabel[]>;
  // City the explore header resolved for the viewport; appended to a tapped
  // district's name so the geocode lands in the right city.
  areaCity?: string | null;
};

type Results = {
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
  formatted_address: string;
  place_id: string;
  types: string[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
    viewport: google.maps.LatLngBounds;
    bounds?: google.maps.LatLngBounds;
    location_type: string;
  };
}[];

const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 25 25" fill="none" aria-hidden="true">
    <path d="M11.1512 3.4301C11.558 3.0233 12.2174 3.0233 12.6242 3.4301C13.0307 3.83691 13.0309 4.49637 12.6242 4.90308L6.06899 11.4583H22.3064C22.8817 11.4583 23.348 11.9246 23.348 12.4999C23.3478 13.075 22.8815 13.5416 22.3064 13.5416H6.06899L12.6242 20.0968C13.0307 20.5036 13.0309 21.163 12.6242 21.5697C12.2175 21.9764 11.558 21.9763 11.1512 21.5697L3.1861 13.6047C2.57597 12.9945 2.576 12.0054 3.1861 11.3952L11.1512 3.4301Z" fill="#1A1A1A" />
  </svg>
);

const SearchGlassIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M9.62467 1.83301C13.9279 1.83301 17.4163 5.32146 17.4163 9.62467C17.4163 11.3629 16.846 12.9674 15.8838 14.2635L19.1431 17.5229C19.5906 17.9703 19.5906 18.6957 19.1431 19.1431C18.6957 19.5906 17.9703 19.5906 17.5229 19.1431L14.2635 15.8838C12.9674 16.846 11.3629 17.4163 9.62467 17.4163C5.32146 17.4163 1.83301 13.9279 1.83301 9.62467C1.83301 5.32146 5.32146 1.83301 9.62467 1.83301ZM9.62467 3.66634C6.33398 3.66634 3.66634 6.33398 3.66634 9.62467C3.66634 12.9154 6.33398 15.583 9.62467 15.583C12.9154 15.583 15.583 12.9154 15.583 9.62467C15.583 6.33398 12.9154 3.66634 9.62467 3.66634Z" fill="#333333" />
  </svg>
);

const ClearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M13.1689 0.335607C13.6164 -0.111869 14.3417 -0.111869 14.7892 0.335607C15.2366 0.78309 15.2367 1.50844 14.7892 1.95589L9.18269 7.56241L14.7892 13.1689C15.2366 13.6164 15.2367 14.3418 14.7892 14.7892C14.3418 15.2367 13.6164 15.2366 13.1689 14.7892L7.56241 9.18269L1.95589 14.7892C1.50844 15.2367 0.78309 15.2366 0.335607 14.7892C-0.111868 14.3417 -0.111869 13.6164 0.335607 13.1689L5.94213 7.56241L0.335607 1.95589C-0.111869 1.50841 -0.111869 0.783083 0.335607 0.335607C0.783083 -0.111869 1.50841 -0.111869 1.95589 0.335607L7.56241 5.94213L13.1689 0.335607Z" fill="#666666" />
  </svg>
);

const SearchInput = ({
  setMapCenter,
  setPreferredLocation,
  setTriggeredFromSearch,
  setSearchResultBounds,
  setSearchAddressComponents,
  setCountryOfFeedContent,
  countryOfFeedContent,
  placeholder,
  isActive,
  onActivate,
  onDeactivate,
  debugLabel,
  viewportCenter,
  viewportZoom,
  getNearbyPlaces,
  areaCity,
}: SearchInputProps) => {
  const { t, i18n } = useTranslation();
  const [address, setAddress] = useState('');
  const [isLeavingPill, setIsLeavingPill] = useState(false);
  const [isArrivingPill, setIsArrivingPill] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const pillContentRef = useRef<HTMLSpanElement | null>(null);
  const wasActiveRef = useRef(isActive);
  const arrivingFrameRef = useRef<number | null>(null);
  const leavingTimeoutRef = useRef<number | null>(null);
  const { isLoaded, loadError } = useGoogleMapsApi();

  // JAA-358: district rows seeded from the map centre when the search opens,
  // then re-ranked around the device position once the user taps the location
  // row. `null` = still loading.
  const showDistricts = !!viewportCenter && !!getNearbyPlaces;
  const [districts, setDistricts] = useState<NearbyPlaceLabel[] | null>(null);
  const [located, setLocated] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<GeolocationFailure | null>(null);
  // Bumped on every fetch and on close so a late response never lands on a
  // newer state (map-centre rows over GPS rows, or anything after a close).
  const districtsTokenRef = useRef(0);

  const handleChange = (next: string) => {
    setAddress(next);
  };

  const handleClear = () => {
    setAddress('');
    inputRef.current?.focus();
  };

  const handleClose = () => {
    setAddress('');
    inputRef.current?.blur();
    onDeactivate();
  };

  const handleSelect = (selected: string) => {
    if (debugLabel) {
      console.log(`[${debugLabel}] address search selected`, {
        selectedAddress: selected,
        searchInputValueBeforeSelect: address,
      });
    }

    setAddress(selected);
    geocodeByAddress(selected)
      .then(async (results: Results) => {
        if (results.length === 0) {
          console.warn('No results found for the given address.');
          return;
        }
        const result = results[0];
        const latLng = await getLatLng(result);
        let selectedBounds: SearchResultBounds | null = null;

        // Forward the raw `address_components` so the parent can derive its
        // own area label (neighborhood / locality / postal / city). Same
        // shape is reused on the map-pan reverse-geocode path so both flows
        // run through the same parser.
        if (setSearchAddressComponents) {
          setSearchAddressComponents(result.address_components);
        }

        // Prefer the place's actual rectangle (`geometry.bounds`) over Google's
        // display heuristic (`geometry.viewport`). `bounds` is returned for
        // areas large enough to have a meaningful bounding box (cities,
        // districts, neighborhoods, regions, countries) and it gives a
        // consistent fit across same-typed places. Falls back to `viewport`
        // for points (buildings, POIs). The per-type `maxZoom` cap protects
        // the rare case where `bounds` is missing AND `viewport` happens to
        // be tighter than the place would warrant.
        const geometry = result.geometry as {
          bounds?: google.maps.LatLngBounds;
          viewport?: google.maps.LatLngBounds;
        };

        const primaryRect: google.maps.LatLngBounds | undefined =
          rectIsRich(geometry.bounds)
            ? geometry.bounds
            : geometry.viewport;
        let pickedFrom: 'bounds' | 'viewport' | 'postal_code_secondary' | 'none' =
          rectIsRich(geometry.bounds)
            ? 'bounds'
            : geometry.viewport
              ? 'viewport'
              : 'none';

        // Some Helsinki neighborhoods (Sörnäinen, Käpylä, ...) come back
        // without `bounds` and with Google's default ~0.0027 deg "no extent
        // known" viewport. The address_components still carry the right
        // postal_code, and Helsinki postal areas align with the named
        // neighborhoods, so a secondary geocode by postal_code recovers the
        // actual extent.
        let secondaryQuery: string | null = null;
        let secondaryRect: google.maps.LatLngBounds | undefined;
        const primaryIsTinyArea =
          isAreaResultType(result.types)
          && !geometry.bounds
          && !rectIsRich(geometry.viewport);

        if (primaryIsTinyArea) {
          const postalComponent = findComponent(result.address_components, 'postal_code');
          if (postalComponent) {
            const localityComponent = findComponent(result.address_components, 'locality');
            const countryComponent = findComponent(result.address_components, 'country');
            secondaryQuery = [
              postalComponent.long_name,
              localityComponent?.long_name,
              countryComponent?.long_name,
            ]
              .filter((part): part is string => Boolean(part))
              .join(' ');
            try {
              const secondaryResults = (await geocodeByAddress(secondaryQuery)) as Results;
              if (secondaryResults.length > 0) {
                const secondaryGeo = secondaryResults[0].geometry as {
                  bounds?: google.maps.LatLngBounds;
                  viewport?: google.maps.LatLngBounds;
                };
                const candidate = secondaryGeo.bounds ?? secondaryGeo.viewport;
                if (candidate && rectIsRich(candidate)) {
                  // Floor sanity check; postal areas in Helsinki are reliably
                  // larger than this. Defends against an unlikely degenerate
                  // secondary result that's also tiny.
                  const cNe = candidate.getNorthEast();
                  const cSw = candidate.getSouthWest();
                  const cLngSpan = cNe.lng() - cSw.lng();
                  const cLatSpan = cNe.lat() - cSw.lat();
                  if (
                    cLngSpan >= SECONDARY_RECT_MIN_SPAN_DEG
                    || cLatSpan >= SECONDARY_RECT_MIN_SPAN_DEG
                  ) {
                    secondaryRect = candidate;
                  }
                }
              }
            } catch (secondaryErr) {
              if (debugLabel) {
                console.warn(
                  `[${debugLabel}] postal_code secondary geocode failed`,
                  secondaryErr,
                );
              }
            }
          }
        }

        const finalRect = secondaryRect ?? primaryRect;
        if (secondaryRect) {
          pickedFrom = 'postal_code_secondary';
        }

        if (finalRect) {
          const ne = finalRect.getNorthEast();
          const sw = finalRect.getSouthWest();
          selectedBounds = {
            sw: [sw.lng(), sw.lat()],
            ne: [ne.lng(), ne.lat()],
            maxZoom: computeMaxZoom(result),
          };
        }

        if (debugLabel) {
          // Helper: rectangle dimensions for diagnostics.
          const describeRect = (
            r: google.maps.LatLngBounds | undefined,
          ): {
            sw: [number, number];
            ne: [number, number];
            lngSpan: number;
            latSpan: number;
          } | null => {
            if (!r) return null;
            const rne = r.getNorthEast();
            const rsw = r.getSouthWest();
            return {
              sw: [rsw.lng(), rsw.lat()],
              ne: [rne.lng(), rne.lat()],
              lngSpan: rne.lng() - rsw.lng(),
              latSpan: rne.lat() - rsw.lat(),
            };
          };
          console.log(`[${debugLabel}] address search geocode result`, {
            selectedAddress: selected,
            searchInputValueBeforeSelect: address,
            formattedAddress: result.formatted_address,
            placeId: result.place_id,
            resultTypes: result.types,
            latLng,
            bounds: selectedBounds,
            pickedFrom,
            rawBounds: describeRect(geometry.bounds),
            rawViewport: describeRect(geometry.viewport),
            secondaryQuery,
            secondaryRect: describeRect(secondaryRect),
            computedMaxZoom: selectedBounds?.maxZoom ?? null,
            addressComponents: result.address_components,
          });
        }

        if (setSearchResultBounds) {
          setSearchResultBounds(selectedBounds);
        }

        setTriggeredFromSearch(true);
        setMapCenter(latLng);
        // A postal-code fallback or display viewport can frame the map, but
        // neither describes the selected place's own extent for pan expiry.
        setPreferredLocation?.(
          preferredLocationFromResult(
            result,
            latLng,
            !secondaryRect && rectIsRich(geometry.bounds)
              ? selectedBounds
              : null,
          ),
        );
        for (const component of result.address_components) {
          if (component.types.includes('country')) {
            if (component.long_name !== countryOfFeedContent) {
              setCountryOfFeedContent(component.long_name);
            }
          }
        }
      })
      .catch((error: any) => {
        console.error('Error', error);
      })
      .finally(() => {
        inputRef.current?.blur();
        onDeactivate();
        // The deactivate effect also clears `address`, but set it here too so
        // the input is empty even if the parent doesn't unmount the active
        // branch immediately.
        setAddress('');
      });
  };

  // Bring the map to the user's position (about a kilometre across, capped at
  // street level) without closing the search, so the rows and the map agree.
  const flyToPoint = (point: LatLng) => {
    const dLat = 0.0045;
    const dLng = dLat / Math.cos((point.lat * Math.PI) / 180);
    setSearchResultBounds?.({
      sw: [point.lng - dLng, point.lat - dLat],
      ne: [point.lng + dLng, point.lat + dLat],
      maxZoom: NEIGHBORHOOD_MAX_ZOOM + 1,
    });
    setTriggeredFromSearch(true);
    setMapCenter(point);
    setPreferredLocation?.(null);
  };

  // Ask for the device position, replace the rows with the districts around it
  // and fly the map there; tapping again simply refreshes the fix. The token is
  // only bumped once a fix arrives, so a refused prompt
  // leaves the map-centre rows (possibly still loading) in place; rows and
  // `located` commit together so the subtitles never mix a GPS header with
  // map-centre distances.
  const handleUseCurrentLocation = async () => {
    let token = districtsTokenRef.current;
    setLocationError(null);
    setIsLocating(true);
    try {
      const point = await getCurrentPosition();
      if (districtsTokenRef.current !== token || !getNearbyPlaces) return;
      token = (districtsTokenRef.current += 1);
      flyToPoint(point);
      const labels = await getNearbyPlaces(point);
      if (districtsTokenRef.current !== token) return;
      setDistricts(pickDistrictRows(labels));
      setLocated(true);
    } catch (err) {
      if (districtsTokenRef.current === token) setLocationError(toGeolocationFailure(err));
    } finally {
      if (districtsTokenRef.current === token) setIsLocating(false);
    }
  };

  // A district row is a search for that name: the same Google geocode + fit the
  // typed search does, with the header's city appended so the geocode lands in
  // the right city.
  const handleSelectDistrict = (district: NearbyPlaceLabel) => {
    handleSelect([district.name, areaCity].filter(Boolean).join(', '));
  };

  // Subtitle under a district row once the user has shared their position. The
  // labels are points, not polygons, so "here" is the nearest one within a
  // district's own scale.
  const districtSubtitle = (district: NearbyPlaceLabel): string | null => {
    if (!located) return null;
    if (district === districts?.[0] && district.distanceM <= HERE_MAX_M) {
      return t('newLandingPage.explore.youAreHere');
    }
    const km = (Math.max(100, district.distanceM) / 1000).toLocaleString(i18n.language, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return t('newLandingPage.explore.kmAway', { km });
  };

  // Copy for a failed location request: what went wrong and what to do next.
  const locationErrorCopy = (failure: GeolocationFailure): string => {
    if (failure === 'denied') return t('newLandingPage.explore.locationDenied');
    if (failure === 'insecure') return t('newLandingPage.explore.locationInsecure');
    return t('newLandingPage.explore.locationUnavailable');
  };

  // Title + subtitle of the location row across idle / locating / located.
  const locationRowCopy = (): { title: string; subtitle: string | null } => {
    if (isLocating) {
      return { title: t('newLandingPage.addMyPlace.address.locating'), subtitle: null };
    }
    if (located) {
      return {
        title: t('newLandingPage.explore.usingYourLocation'),
        subtitle: districts?.[0]?.name ?? null,
      };
    }
    return {
      title: t('newLandingPage.addMyPlace.address.useCurrentLocation'),
      subtitle: null,
    };
  };
  const locationRow = locationRowCopy();

  // Caption above the district rows: loading until fetched, then which point the
  // rows are ranked around. An empty result says nothing at all — the location
  // row is the whole block then, and a caption over no rows only adds noise.
  const districtsCaption = (): string | null => {
    if (!districts) return t('newLandingPage.explore.loadingDistricts');
    // Nothing nearby: the block offers the city where Jaakko has listings
    // instead, so a viewer far from any covered district still has somewhere
    // to go. Saying "no districts found" left them with only a dead end.
    if (districts.length === 0) return showShowcase ? t('newLandingPage.explore.showcaseCaption') : null;
    if (located) return t('newLandingPage.explore.districtsNearYou');
    return t('newLandingPage.explore.suggestedDistricts');
  };
  // Finland is skipped: the country is full of listings, so a viewer there whose
  // own area has no districts to suggest is better served by the map they are
  // already looking at than by a trip to Helsinki.
  const showShowcase = districts?.length === 0 && countryOfFeedContent !== 'Finland';
  const caption = districtsCaption();
  const listingTotal = useListingTotal();

  useEffect(() => {
    if (!isActive) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      inputRef.current?.focus();
    }
  }, [isActive]);

  // On close, clear the input and the district block so the next open starts
  // fresh (catches every close path: back arrow, click-outside, suggestion
  // select). On open, seed the district rows from the map centre captured at
  // that moment; a map pan while the search is open keeps the rows.
  useEffect(() => {
    const token = (districtsTokenRef.current += 1);
    if (!isActive) {
      setAddress('');
      setDistricts(null);
      setLocated(false);
      setIsLocating(false);
      setLocationError(null);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Rows follow the map: panning and zooming both change what the viewer is
  // looking at, so leaving the rows on the centre captured at open time offered
  // districts from wherever they started. Debounced, because a drag or a pinch
  // reports a new viewport many times before it settles. GPS rows are left
  // alone: once the viewer has asked for their own position, the map moving
  // under the open sheet must not take it away from them.
  useEffect(() => {
    if (!isActive || located || !showDistricts || !viewportCenter || !getNearbyPlaces) return undefined;
    const settleMs = districts === null ? 0 : 400;
    const timer = window.setTimeout(() => {
      const token = (districtsTokenRef.current += 1);
      getNearbyPlaces(viewportCenter).then((labels) => {
        if (districtsTokenRef.current === token) setDistricts(pickDistrictRows(labels));
      });
    }, settleMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, located, viewportCenter?.lat, viewportCenter?.lng, viewportZoom]);

  // Trigger the "slide back to center" animation when transitioning from
  // active back to inactive. The pill mounts with --arriving (content snapped
  // to the left, matching where the active input was), and we then drop the
  // class on the next frame so the spacer transitions back to flex-grow: 1.
  useLayoutEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (!wasActive || isActive) return undefined;
    setIsArrivingPill(true);
    const handle1 = window.requestAnimationFrame(() => {
      const handle2 = window.requestAnimationFrame(() => {
        setIsArrivingPill(false);
      });
      arrivingFrameRef.current = handle2;
    });
    return () => {
      window.cancelAnimationFrame(handle1);
      if (arrivingFrameRef.current !== null) {
        window.cancelAnimationFrame(arrivingFrameRef.current);
        arrivingFrameRef.current = null;
      }
    };
  }, [isActive]);

  useEffect(() => () => {
    if (leavingTimeoutRef.current !== null) {
      window.clearTimeout(leavingTimeoutRef.current);
    }
    if (arrivingFrameRef.current !== null) {
      window.cancelAnimationFrame(arrivingFrameRef.current);
    }
  }, []);

  // Measure the pill and its inner content so the centered position can be
  // expressed as a `transform: translateX(...)` driven by a CSS variable.
  // Animating `transform` (instead of the `::before` flex-grow that this
  // replaced) is sub-pixel precise and lands cleanly at the end of the
  // transition without the layout-rounding "rattle".
  useLayoutEffect(() => {
    if (isActive) return undefined;
    const pill = pillRef.current;
    const content = pillContentRef.current;
    if (!pill || !content) return undefined;

    const updateShift = () => {
      if (!pill.isConnected) return;
      const styles = window.getComputedStyle(pill);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const interiorWidth = pill.clientWidth - paddingLeft - paddingRight;
      const contentWidth = content.offsetWidth;
      // Round to integer so the content lands on a whole pixel at rest;
      // sub-pixel anti-aliasing changes between motion and rest can otherwise
      // read as a tiny "snap" at the end of the transition.
      const shift = Math.max(0, Math.round((interiorWidth - contentWidth) / 2));
      pill.style.setProperty('--new-search-pill-center-shift', `${shift}px`);
    };

    updateShift();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateShift);
      return () => window.removeEventListener('resize', updateShift);
    }

    const observer = new ResizeObserver(updateShift);
    observer.observe(pill);
    observer.observe(content);
    return () => observer.disconnect();
  }, [isActive, isLoaded]);

  const handlePillClick = () => {
    if (isLeavingPill) return;
    setIsLeavingPill(true);
    leavingTimeoutRef.current = window.setTimeout(() => {
      onActivate();
      setIsLeavingPill(false);
      leavingTimeoutRef.current = null;
    }, 220);
  };

  const resolvedPlaceholder = placeholder ?? t('newLandingPage.explore.search');

  if (loadError) {
    return (
      <div className="new-search new-search--error">
        Error loading map services: {loadError.message}
      </div>
    );
  }

  if (!isActive) {
    const pillClassName = [
      'new-search__pill',
      isLeavingPill ? 'new-search__pill--leaving' : '',
      isArrivingPill ? 'new-search__pill--arriving' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div
        ref={wrapperRef}
        className={`new-search ${!isLoaded ? 'new-search--loading' : ''}`}
      >
        <button
          type="button"
          ref={pillRef}
          className={pillClassName}
          onClick={handlePillClick}
          aria-label={resolvedPlaceholder}
          disabled={!isLoaded || isLeavingPill}
        >
          <span className="new-search__pill-content" ref={pillContentRef}>
            <span className="new-search__icon">
              <SearchGlassIcon />
            </span>
            <span className="new-search__pill-label">{resolvedPlaceholder}</span>
          </span>
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="new-search new-search--active">
      <button
        type="button"
        className="new-search__close"
        onClick={handleClose}
        aria-label={t('newLandingPage.explore.closeSearchAria')}
      >
        <BackArrowIcon />
      </button>
      <PlacesAutocomplete
        value={address}
        onChange={handleChange}
        onSelect={handleSelect}
        searchOptions={placesSearchOptions(viewportCenter)}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
          // Google rows only while there is text: the library keeps (and can
          // still deliver) predictions after the clear button empties the field.
          const showResults = !!address && (loading || suggestions.length > 0);
          return (
            <div className="new-search__column">
              <div className="new-search__field">
                <span className="new-search__icon">
                  <SearchGlassIcon />
                </span>
                <input
                  {...getInputProps({
                    placeholder: resolvedPlaceholder,
                    className: 'new-search__input',
                  })}
                  ref={(node) => {
                    inputRef.current = node;
                  }}
                />
                {address && (
                  <button
                    type="button"
                    className="new-search__clear"
                    onClick={handleClear}
                    aria-label={t('newLandingPage.explore.clearSearchAria')}
                  >
                    <ClearIcon />
                  </button>
                )}
              </div>
              {showResults && (
                <div className="new-search__results">
                  {loading && (
                    <div className="new-search__result new-search__result--loading">
                      {t('common.loading')}
                    </div>
                  )}
                  {suggestions.map((suggestion) => {
                    const suggestionProps = getSuggestionItemProps(suggestion, {
                      className: `new-search__result ${suggestion.active ? 'new-search__result--active' : ''}`,
                    });
                    return (
                      <div key={suggestion.placeId} {...suggestionProps}>
                        <span className="new-search__result-text">
                          {suggestion.description}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {!address && showDistricts && (
                <div className="new-search__results new-search__results--districts">
                  <button
                    type="button"
                    className={`new-search__row new-search__row--location${located ? ' new-search__row--located' : ''}`}
                    onClick={handleUseCurrentLocation}
                    disabled={isLocating}
                  >
                    <span className="new-search__row-icon" aria-hidden="true">
                      <LocationArrowIcon />
                    </span>
                    <span className="new-search__row-text">
                      <span className="new-search__row-title">{locationRow.title}</span>
                      {locationRow.subtitle && (
                        <span className="new-search__row-subtitle">{locationRow.subtitle}</span>
                      )}
                    </span>
                  </button>
                  {locationError && (
                    <p className="new-search__error" role="alert">
                      {locationErrorCopy(locationError)}
                    </p>
                  )}
                  {caption && <p className="new-search__group-label">{caption}</p>}
                  {showShowcase && (
                    <button
                      type="button"
                      className="new-search__row"
                      onClick={() => handleSelect(SHOWCASE_CITY)}
                    >
                      <span className="new-search__row-icon" aria-hidden="true">
                        <BuildingChipIcon color="currentColor" />
                      </span>
                      <span className="new-search__row-text">
                        <span className="new-search__row-title">
                          {t('newLandingPage.explore.showcaseCity', {
                            total: listingTotal.toLocaleString(
                              i18n.language?.startsWith('fi') ? 'fi-FI' : 'en-US',
                            ),
                          })}
                        </span>
                      </span>
                    </button>
                  )}
                  {districts?.map((district) => {
                    const subtitle = districtSubtitle(district);
                    return (
                      <button
                        key={district.name}
                        type="button"
                        className="new-search__row"
                        onClick={() => handleSelectDistrict(district)}
                      >
                        <span className="new-search__row-icon" aria-hidden="true">
                          <DistrictChipIcon />
                        </span>
                        <span className="new-search__row-text">
                          <span className="new-search__row-title">{district.name}</span>
                          {subtitle && <span className="new-search__row-subtitle">{subtitle}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }}
      </PlacesAutocomplete>
    </div>
  );
};

export default SearchInput;
